import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Briefcase, Cake, Hash, Loader2, MapPin, MessageCircle, Music, Phone, StickyNote, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { ImageUploadField } from '@/components/ui/image-upload'
import { Separator } from '@/components/ui/separator'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useCustomer, useUpdateCustomer } from '@/hooks/useCustomers'
import { createCustomerSchema, type CreateCustomerData } from '@/schemas/customer'
import type { ContactType, ContactInput, CreateCustomerRequest, Customer } from '@/types/customer'
import { customerCategoriesApi } from '@/api/customer-categories'
import { CUSTOMER_CATEGORY_TYPES, VIP_LEVEL_OPTIONS } from '@/types/customer-category'
import { CityCascadeSelect, useCityCascadeInit } from '@/components/common/CityCascadeSelect'
import { showError } from '@/lib/error'

interface EditCustomerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  customer: Customer | null
  onSuccess?: () => void
}

const defaultContacts: ContactInput[] = [
  { type: 1 as ContactType, key: 'mobile_1', value: '' },
  { type: 1 as ContactType, key: 'mobile_2', value: '' },
  { type: 2 as ContactType, key: 'wechat_1', value: '' },
  { type: 2 as ContactType, key: 'wechat_2', value: '' },
]

function normalizeVipLevel(value: number | undefined) {
  return VIP_LEVEL_OPTIONS.some(option => option.value === value) ? value : 0
}

function toDateInputValue(value?: string | null) {
  if (!value) return ''
  return value.slice(0, 10)
}

export function EditCustomerDialog({
  open,
  onOpenChange,
  customer,
  onSuccess,
}: EditCustomerDialogProps) {
  const updateMutation = useUpdateCustomer()
  const { data: detailCustomer } = useCustomer(customer?.id || 0, open && !!customer)
  const { data: categoryOptions = [] } = useQuery({
    queryKey: ['customer-categories', 'options'],
    queryFn: customerCategoriesApi.options,
    enabled: open,
  })
  const activeCustomer = detailCustomer || customer

  const form = useForm<CreateCustomerData>({
    resolver: zodResolver(createCustomerSchema),
    defaultValues: {
      name: '',
      gender: '0',
      province: '',
      city: '',
      district: '',
      city_id: undefined,
      tiktok: '',
      age: 0,
      job: '',
      family: '',
      birthday: '',
      blacklist: false,
      pic_1: '',
      pic_2: '',
      address: '',
      remark: '',
      category_id: undefined,
      vip_level: 99,
      contacts: defaultContacts,
    },
  })

  const { initData, reset: resetCascade } = useCityCascadeInit(activeCustomer?.city_id)
  const selectedCategoryId = form.watch('category_id')
  const selectedCategory = categoryOptions.find(category => category.id === selectedCategoryId) ||
    (activeCustomer && activeCustomer.category_id === selectedCategoryId ? activeCustomer.category : undefined)
  const showVipLevel = !!selectedCategory && selectedCategory.type !== 0

  // 当客户数据变化时更新表单
  useEffect(() => {
    if (open && activeCustomer) {
      // 构建联系方式数组
      const contacts: ContactInput[] = [
        { type: 1 as ContactType, key: 'mobile_1', value: '' },
        { type: 1 as ContactType, key: 'mobile_2', value: '' },
        { type: 2 as ContactType, key: 'wechat_1', value: '' },
        { type: 2 as ContactType, key: 'wechat_2', value: '' },
      ]

      // 填充现有联系方式
      activeCustomer.contacts.forEach(c => {
        const idx = contacts.findIndex(cc => cc.key === c.key)
        if (idx !== -1) {
          contacts[idx].value = c.value
        }
      })

      form.reset({
        name: activeCustomer.name,
        gender: String(activeCustomer.gender) as '0' | '1' | '2',
        tiktok: activeCustomer.tiktok || '',
        age: activeCustomer.age || 0,
        job: activeCustomer.job || '',
        family: activeCustomer.family || '',
        birthday: toDateInputValue(activeCustomer.birthday),
        blacklist: !!activeCustomer.blacklist,
        pic_1: activeCustomer.pic_1 || '',
        pic_2: activeCustomer.pic_2 || '',
        province: activeCustomer.province || '',
        city: activeCustomer.city || '',
        district: activeCustomer.district || '',
        address: activeCustomer.address || '',
        remark: activeCustomer.remark || '',
        city_id: activeCustomer.city_id || undefined,
        category_id: activeCustomer.category_id ?? undefined,
        vip_level: activeCustomer.vip_level ?? 99,
        contacts,
      })
    }
  }, [open, activeCustomer, form])

  const onSubmit = async (data: CreateCustomerData) => {
    if (!customer) return

    try {
      // 过滤掉空的联系方式（后端要求 value 不能为空）
      const filteredContacts = data.contacts.filter(c => c.value && c.value.trim() !== '')

      // 转换 gender 为数字类型以符合 API 要求
      const submitData: CreateCustomerRequest = {
        ...data,
        gender: data.gender ? (Number(data.gender) as 0 | 1 | 2) : undefined,
        category_id: data.category_id ?? 0,
        vip_level: showVipLevel ? normalizeVipLevel(data.vip_level) : 99,
        contacts: filteredContacts,
      }
      await updateMutation.mutateAsync({ id: customer.id, data: submitData })
      form.reset()
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      showError(error, '更新客户失败')
    }
  }

  const handleClose = () => {
    form.reset()
    resetCascade()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-5xl flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl">
        <DialogHeader className="shrink-0 border-b px-6 py-5">
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            编辑客户
          </DialogTitle>
          <DialogDescription>
            修改客户信息
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
              <div className="space-y-4">
                <h4 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <User className="h-4 w-4" />
                  基础信息
                </h4>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>姓名 <span className="text-destructive">*</span></FormLabel>
                        <FormControl>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input placeholder="请输入客户姓名" className="pl-9" {...field} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="gender"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>性别</FormLabel>
                        <FormControl>
                          <select
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            value={field.value}
                            onChange={field.onChange}
                          >
                            <option value="0">未知</option>
                            <option value="1">男</option>
                            <option value="2">女</option>
                          </select>
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="tiktok"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>抖音号</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Music className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input placeholder="请输入抖音号" className="pl-9" {...field} value={field.value ?? ''} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="age"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>年龄</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Hash className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              type="number"
                              min={0}
                              placeholder="请输入年龄"
                              className="pl-9"
                              value={field.value ?? 0}
                              onChange={(event) => field.onChange(Number(event.target.value || 0))}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="job"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>职业</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Briefcase className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input placeholder="请输入职业" className="pl-9" {...field} value={field.value ?? ''} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="birthday"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>生日</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Cake className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input type="date" className="pl-9" {...field} value={field.value ?? ''} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="blacklist"
                    render={({ field }) => (
                      <FormItem className="flex h-10 items-center gap-3 rounded-md border px-3 py-2">
                        <FormControl>
                          <Checkbox checked={!!field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className="m-0">黑名单</FormLabel>
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="category_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>客户类型</FormLabel>
                        <FormControl>
                          <select
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            value={field.value ?? ''}
                            onChange={(event) => {
                              const nextValue = event.target.value ? Number(event.target.value) : undefined
                              const nextCategory = categoryOptions.find(category => category.id === nextValue)
                              field.onChange(nextValue)
                              form.setValue(
                                'vip_level',
                                nextCategory && nextCategory.type !== 0
                                  ? normalizeVipLevel(form.getValues('vip_level'))
                                  : 99,
                              )
                            }}
                          >
                            <option value="">不选择</option>
                            {categoryOptions.map(category => {
                              const typeLabel = CUSTOMER_CATEGORY_TYPES.find(type => type.value === category.type)?.label
                              return (
                                <option key={category.id} value={category.id}>
                                  {category.name}{typeLabel ? `（${typeLabel}）` : ''}
                                </option>
                              )
                            })}
                          </select>
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {showVipLevel && (
                    <FormField
                      control={form.control}
                      name="vip_level"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>VIP等级</FormLabel>
                          <FormControl>
                            <select
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              value={normalizeVipLevel(field.value)}
                              onChange={(event) => field.onChange(Number(event.target.value))}
                            >
                              {VIP_LEVEL_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  )}
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <MessageCircle className="h-4 w-4" />
                  联系方式
                </h4>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="contacts.0.value"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>手机号1 <span className="text-destructive">*</span></FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input placeholder="手机号1" className="pl-9" {...field} value={field.value ?? ''} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="contacts.1.value"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>手机号2</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input placeholder="手机号2" className="pl-9" {...field} value={field.value ?? ''} />
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="contacts.2.value"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>微信号1 <span className="text-destructive">*</span></FormLabel>
                        <FormControl>
                          <div className="relative">
                            <MessageCircle className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input placeholder="微信号1" className="pl-9" {...field} value={field.value ?? ''} />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="contacts.3.value"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>微信号2</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <MessageCircle className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input placeholder="微信号2" className="pl-9" {...field} value={field.value ?? ''} />
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  地址信息
                </h4>
                <CityCascadeSelect
                  defaultProvinceId={initData?.provinceId}
                  defaultCityId={initData?.cityId}
                  defaultDistrictId={initData?.districtId}
                  onChange={(data) => {
                    if (data) {
                      form.setValue('province', data.province)
                      form.setValue('city', data.city)
                      form.setValue('district', data.district)
                      form.setValue('city_id', data.cityId || undefined)
                    } else {
                      form.setValue('province', '')
                      form.setValue('city', '')
                      form.setValue('district', '')
                      form.setValue('city_id', undefined)
                    }
                  }}
                />

                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>详细地址</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input placeholder="详细地址" className="pl-9" {...field} />
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <Separator />

              <div className="space-y-4">
                <h4 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <StickyNote className="h-4 w-4" />
                  补充信息
                </h4>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="remark"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>备注</FormLabel>
                        <FormControl>
                          <textarea
                            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                            placeholder="请输入备注（可选）"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="family"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>家庭情况</FormLabel>
                        <FormControl>
                          <Textarea placeholder="请输入家庭情况" {...field} value={field.value ?? ''} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="pic_1"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>客户图片1</FormLabel>
                        <FormControl>
                          <ImageUploadField value={field.value || ''} onChange={field.onChange} placeholder="图片1" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="pic_2"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>客户图片2</FormLabel>
                        <FormControl>
                          <ImageUploadField value={field.value || ''} onChange={field.onChange} placeholder="图片2" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="shrink-0 gap-2 border-t px-6 py-4 sm:gap-0">
              <Button type="button" variant="outline" onClick={handleClose}>
                取消
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                保存
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
